import { useCallback } from 'react';
import { ApolloCache } from '@apollo/client';

import {
	PostCommentFragment,
	PostCommentFragmentDoc,
} from '%fragments/__generated__/postData.generated';
import { useUser } from '%hooks';
import { Comment } from '../types';
import {
	useLikeCommentMutation,
	useRemoveLikeFromCommentMutation,
} from './__generated__/comment.mutation.generated';

export const useCommentLikes = (comment: Comment) => {
	const user = useUser();

	const likesCount = comment.likes?.length;
	const userLike = comment.likes?.find((like) => like.author.id === user.id.toString());
	const isLikedByUser = Boolean(userLike);

	const [like, { loading: isAdding }] = useLikeCommentMutation({
		variables: { commentId: comment.id },
		optimisticResponse: {
			__typename: 'Mutation',
			likeComment: {
				__typename: 'CommentLike',
				id: `temp-like-comment:${comment.id}`,
				createdAt: Date.now(),
				author: {
					__typename: 'User',
					...user,
				},
			},
		},
		update: (cache, result) => {
			return updateCommentCache(cache, comment.id, (fragmentData) => {
				const newLike = result.data?.likeComment;
				if (!newLike) {
					return fragmentData;
				}

				return {
					...fragmentData,
					likes: [
						...(fragmentData.likes || []).filter((like) => like.author.id !== newLike.author.id),
						newLike,
					],
				};
			});
		},
	});

	const [removeLike, { loading: isRemoving }] = useRemoveLikeFromCommentMutation({
		variables: { likeId: userLike?.id || '' },
		optimisticResponse: ({ likeId }) => ({
			__typename: 'Mutation',
			removeLikeFromComment: {
				__typename: 'CommentLike',
				id: likeId,
			},
		}),
		update: (cache, result) => {
			return updateCommentCache(cache, comment.id, (fragmentData) => {
				const removedLike = result.data?.removeLikeFromComment;
				if (!removedLike) {
					return fragmentData;
				}

				return {
					...fragmentData,
					likes: (fragmentData.likes || []).filter((like) => like.id !== removedLike.id),
				};
			});
		},
	});

	const disabledLiking = isAdding || isRemoving;

	const toggleLike = useCallback(() => {
		isLikedByUser ? void removeLike() : void like();
	}, [isLikedByUser, like, removeLike]);

	return {
		likesCount,
		isLikedByUser,
		disabledLiking,
		toggleLike,
	};
};

function updateCommentCache(
	cache: ApolloCache<object>,
	commentId: Comment['id'],
	updateLogic: (fragmentData: PostCommentFragment) => PostCommentFragment,
) {
	return cache.updateFragment<PostCommentFragment>(
		{
			id: `Comment:${commentId}`,
			fragment: PostCommentFragmentDoc,
			fragmentName: 'PostComment',
		},
		(fragmentData) => {
			if (!fragmentData) {
				return fragmentData;
			}

			return updateLogic(fragmentData);
		},
	);
}
